import { Trash } from '@griply/assets';
import { useDeleteSmartList } from 'features/smart-lists';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PenEdit16 } from 'shared/assets/icons';
import { SmartListDeleteDialog } from 'shared/components/connected/smart-list-delete-dialog';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuToggle,
} from 'shared/components/ui/popup-menu';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { SmartList } from 'shared/types/smart-list';

type SmartListContextMenuProps = {
  smartList: SmartList;
  referenceElement: React.RefObject<HTMLElement>;
  onClose: () => void;
  onEdit: () => void;
  showCompleted?: boolean;
  onToggleShowCompleted?: () => void;
  open: boolean;
};

export const SmartListContextMenu: React.FC<SmartListContextMenuProps> = ({
  smartList,
  referenceElement,
  onClose,
  onEdit,
  showCompleted,
  onToggleShowCompleted,
  open,
}) => {
  const { t } = useTranslation();
  const { submit: deleteSmartList, isPending } = useDeleteSmartList();
  const {
    menuOpen: deleteDialogOpen,
    openMenu: openDeleteDialog,
    closeMenu: closeDeleteDialog,
  } = useOpenMenu();

  const handleToggle = () => {
    onToggleShowCompleted?.();
    onClose();
  };

  const onDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openDeleteDialog();
    onClose();
  };

  const handleDeleteConfirm = async () => {
    await deleteSmartList(smartList);
    closeDeleteDialog();
  };

  const handleEdit = () => {
    onEdit();
    onClose();
  };

  return (
    <>
      {open && (
        <PopupMenu
          referenceElement={referenceElement}
          onOutside={onClose}
          position="bottom-end"
        >
          <PopupMenuList hasBottomBorder>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={handleEdit}
                start={<Icon icon={PenEdit16} />}
              >
                {t('context-menus.smart-list.label.edit')}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
          {showCompleted !== undefined && onToggleShowCompleted && (
            <PopupMenuList hasBottomBorder>
              <PopupMenuListItem>
                <PopupMenuToggle
                  label={t('general.labels.tasks.show-completed-tasks')}
                  onChange={handleToggle}
                  checked={showCompleted}
                />
              </PopupMenuListItem>
            </PopupMenuList>
          )}

          <PopupMenuList>
            <PopupMenuListItem>
              <PopupMenuButton
                onClick={onDeleteClick}
                start={<Icon icon={Trash} />}
                textColor={Colors.Error}
                startColor={Colors.Error}
              >
                {t('general.labels.delete')}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
        </PopupMenu>
      )}

      <SmartListDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
        isPending={isPending}
        smartListName={smartList.name}
      />
    </>
  );
};
